import { TokenListMetadata } from "@bridgesplit/abf-sdk";

import { LendingVaultExpanded } from "./vault";
import { LoopExpanded } from "./loop";
import { LoopscalePointsAction } from "./points";

export interface PartnerInfoResponse {
    data: PartnerInfo[];
    total: number;
}

export interface PartnerInfo {
    partner: Partner;
    defiLlamaIds: string[];
    hasActiveIntegration: boolean;
}
export interface Partner {
    id: string;
    rewardName?: string;
    description?: string;
    logoUrl?: string;
    defiLlamaUrl?: string;
    twitterUsername?: string;
    websiteUrl?: string;
    birdeyeUrl?: string;
    tags?: string;
    feHidden: boolean;
}

export interface FarmingInfoResponse {
    data: FarmingInfo[];
    total: number;
}

// Backend response - raw integration data
export interface FarmingInfo {
    integrationId: string; // currently loop/lend vault ids
    integrationName: string; // loop/lend name (e.g., "USDC Public")
    isFeatured: boolean; // derived from points - we do not have features rewards
    partners: string[]; // unique partner IDs from both rewards and points
    partnerRewards: PartnerReward[]; // vault rewards with attribution
    partnerPoints: PartnerPoint[]; // points integrations
}

// Partner reward attribution
export interface PartnerReward {
    rewardScheduleId: number; // ID from vault_rewards_schedule table
    partnerId: string; // partner who provided this reward
    percentage: string; // e.g., "100.00" (defaults to 100%)
}

// Partner points integration
export interface PartnerPoint {
    id: string;
    partnerId: string;
    actionType: number; // 0 = Lend, 1 = Borrow, 2 = Loop ActionType
    integrationTargetMint: string; // from partners_point_rewards.target_mint - currently loop/lend vault ids
    integrationMultiplier: number; // points multiplier
    isFeatured: boolean;
}

// Regular lend is unsupported
export enum ActionType {
    Lend = "Lend",
    Borrow = "Borrow",
    Loop = "Loop"
}

export enum MetadataTag {
    Token = "Token",
    Boost = "Boost",
    Points = "Points",
    ComingSoon = "Coming Soon"
}
export enum PartnerType {
    Dex = "Dex",
    Perps = "Leverage",
    Derivatives = "Derivatives",
    Staking = "Staking",
    Yield = "Yield",
    Rwa = "RWA"
}

export interface PartnerInfoExpanded extends PartnerInfo {
    partnerTags: PartnerType[];
    metadataTags: MetadataTag[];
}

export interface PartnerPointExpanded extends PartnerPoint {
    partner: PartnerInfoExpanded | undefined;
}

export interface PartnerRewardExpanded extends PartnerReward {
    partner: PartnerInfoExpanded | undefined;
}

// Base interface with common properties
interface BaseFarmingInfo {
    isFeatured: boolean;
    integrationName: string;
    integrationId: string;
    partners: PartnerInfoExpanded[];
    partnerPoints: PartnerPointExpanded[];
    partnerRewards: PartnerRewardExpanded[];
    loopscaleReward?: LoopscalePointsAction;
}

// Discriminated union types
export interface LendFarmingInfo extends BaseFarmingInfo {
    kind: FarmingTypeKind.Lend;
    integrationType: ActionType.Lend;
    lendVault: LendingVaultExpanded;
}

export interface LoopFarmingInfo extends BaseFarmingInfo {
    kind: FarmingTypeKind.Loop;
    integrationType: ActionType.Loop;
    loopVault: LoopExpanded;
}

export interface BorrowFarmingInfo extends BaseFarmingInfo {
    kind: FarmingTypeKind.Borrow;
    integrationType: ActionType.Borrow;
    metadata: TokenListMetadata;
}

// Frontend enriched type - discriminated union
export type FarmingInfoExpanded = LendFarmingInfo | LoopFarmingInfo | BorrowFarmingInfo;

export enum FarmingTypeKind {
    Lend = "lend",
    Loop = "loop",
    Borrow = "borrow"
}
